
<template>
  <main class="talmud-give">
    <div class="form-container ou-donation-form">
      <PaymentForm class="form-horizontal donation-form">
        <div class="tabs" role="tablist" aria-label="Gift type">
          <button
            v-for="gift in giftTypes"
            :key="gift.id"
            type="button"
            class="tab"
            :class="{ active: activeGift === gift.id }"
            @click="selectGift(gift.id)"
          >
            {{ gift.label }}
            <span class="amt">{{ gift.amountLabel }}</span>
          </button>
        </div>

        <section v-if="activeGift === 'general'" class="field-group amount-group">
          <div class="section-label">A gift of any amount</div>
          <p class="helper">Every gift keeps Talmud Daily free, with no paywall and no barrier to the learning.</p>
          <AmountSelector :subunits="false" />
          <FormRow inline>
            <LazyCurrencySelector :currencies="['USD', 'ILS', 'CAD']" />
          </FormRow>
          <ErrorLabel field="amount" />
        </section>

        <section v-if="activeGift === 'day'" class="field-group">
          <div class="section-label">Choose your day</div>
          <p class="helper">Pick any date in the forty-week journey. Jewish holidays are skipped — those dates aren't shown below.</p>
          <select id="day-picker" class="day-select" :value="sponsorshipDate" @change="selectSponsorshipDate">
            <option value="" disabled>Select a date…</option>
            <option value="Mon, Aug 24, 2026 — Foundations, Week 1">Mon, Aug 24, 2026 — Foundations, Week 1</option>
            <option value="Tue, Aug 25, 2026 — Foundations, Week 1">Tue, Aug 25, 2026 — Foundations, Week 1</option>
            <option value="Wed, Aug 26, 2026 — Foundations, Week 1">Wed, Aug 26, 2026 — Foundations, Week 1</option>
            <option value="Thu, Aug 27, 2026 — Foundations, Week 1">Thu, Aug 27, 2026 — Foundations, Week 1</option>
            <option value="Fri, Aug 28, 2026 — Foundations, Week 1 · Voices">Fri, Aug 28, 2026 — Foundations, Week 1 · Voices</option>
            <option value="Mon, Aug 31, 2026 — Foundations, Week 2">Mon, Aug 31, 2026 — Foundations, Week 2</option>
            <option value="Tue, Sep 1, 2026 — Foundations, Week 2">Tue, Sep 1, 2026 — Foundations, Week 2</option>
            <option value="Wed, Sep 2, 2026 — Foundations, Week 2">Wed, Sep 2, 2026 — Foundations, Week 2</option>
            <option value="Thu, Sep 3, 2026 — Foundations, Week 2">Thu, Sep 3, 2026 — Foundations, Week 2</option>
            <option value="Fri, Sep 4, 2026 — Foundations, Week 2 · Voices">Fri, Sep 4, 2026 — Foundations, Week 2 · Voices</option>
            <option value="Mon, Sep 14, 2026 — Zeraim, Brachos">Mon, Sep 14, 2026 — Zeraim, Brachos</option>
            <option value="Tue, Sep 15, 2026 — Zeraim, Brachos">Tue, Sep 15, 2026 — Zeraim, Brachos</option>
            <option value="Wed, Sep 16, 2026 — Zeraim, Brachos">Wed, Sep 16, 2026 — Zeraim, Brachos</option>
            <option value="Thu, Sep 17, 2026 — Zeraim, Brachos">Thu, Sep 17, 2026 — Zeraim, Brachos</option>
            <option value="Fri, Sep 18, 2026 — Zeraim, Brachos · Voices">Fri, Sep 18, 2026 — Zeraim, Brachos · Voices</option>
            <option value="… all sponsor-able days through Jun 5, 2027 (Siyum HaShas)">… all sponsor-able days through Jun 5, 2027 (Siyum HaShas)</option>
          </select>
        </section>

        <section class="field-group">
          <div class="section-label">Dedication</div>
          <p class="helper">
            {{ activeGift === 'day'
              ? 'Sponsor this day in honor of, or in memory of, someone. Optional.'
              : 'Attach your gift to a name or a memory. Optional.' }}
          </p>
          <div class="choice-group">
            <button
              v-for="choice in dedicationChoices"
              :key="choice.id"
              type="button"
              class="choice"
              :class="{ active: dedicationType === choice.id }"
              @click="selectDedication(choice.id)"
            >
              {{ choice.label }}
            </button>
          </div>
          <FormInput
            class="dedication-input"
            field="custom.Dedication"
            placeholder="Whose name?"
          />
        </section>

        <section v-if="activeGift !== 'general'" class="field-group">
          <div class="section-label">Amount</div>
          <div class="fixed-amount">
            <span class="amt">{{ formattedAmount }}</span>
            <span class="amt-note">
              {{ activeGift === 'day'
                ? 'A day of learning — every episode of Talmud Daily on this date carries your dedication.'
                : 'A complete tractate sponsorship supporting the full program.' }}
            </span>
          </div>
        </section>

        <section class="field-group">
          <div class="section-label">Donation frequency</div>
          <PaymentOptions addMonthly />
        </section>

        <section v-if="activeGift !== 'general'" class="field-group">
          <div class="section-label">How should you be listed?</div>
          <p class="helper">This is how the sponsor line appears on the site and in the daily email.</p>
          <FormInput
            field="custom.SponsorName"
            placeholder="e.g. The Smith Family, or John & Sarah Cohen"
          />
        </section>

        <FormSection title="Your info" class="field-group">
          <FormRow inline>
            <FormCol field="title" :flex="1" />
            <FormCol field="firstName" :flex="2" />
            <FormCol field="lastName" :flex="2" />
          </FormRow>
          <FormRow inline>
            <FormCol field="email" />
            <FormCol field="phone" />
          </FormRow>
          <FormRow inline>
            <Consent privacy="https://ncsy.org/privacy-policy/" />
          </FormRow>
        </FormSection>

        <FormSection title="Billing information" class="field-group">
          <DonorAddress />
        </FormSection>

        <section class="field-group">
          <div class="section-label">Notes</div>
          <FormInput type="textarea" field="custom.Notes" placeholder="Anything else you'd like us to know?" />
        </section>

        <FormSection title="Payment information" class="field-group payment-section" last>
          <FormRow>
            <Receipt :receipts="['us', 'israel', 'canadian']" />
          </FormRow>
          <FormRow inline>
            <FormCol field="card" :showLabel="false" />
          </FormRow>
        </FormSection>

        <div class="submit-row">
          <p class="submit-note">Review your gift, then continue to secure payment.</p>
          <PaymentButton showAmount btnText="Continue to payment" />
        </div>
      </PaymentForm>
    </div>

    <aside class="preview-col">
      <div class="preview">
        <div class="p-eyebrow">Preview</div>
        <h2 class="p-title">How your gift will appear.</h2>
        <p class="p-sub">On the episode page · in the daily email · on the archive</p>
        <div class="p-panel">
          <div class="p-panel-label">{{ activeGift === 'day' ? sponsorshipDate : 'Talmud Daily' }}</div>
          <p class="p-line">Sponsored by <em>{{ donorName }}</em>.</p>
          <p v-if="dedicationText" class="p-dedication">{{ dedicationText }}</p>
        </div>
        <p class="p-note">
          Add a dedication to see it here. General gifts support the program and remain private.
        </p>
        <div class="amt-summary">
          <span>Your gift</span>
          <span class="total">{{ formattedAmount }}</span>
        </div>
      </div>
    </aside>
  </main>
</template>
<script lang="ts">
import {
  computed,
  defineComponent,
  ref
} from 'vue'
import { usePaymentForm } from '~/shared/use-payment-form'

export default defineComponent({
  setup() {
    const { setValue, form } = usePaymentForm()

    const activeGift = ref<'day' | 'tractate' | 'general'>('day')
    const dedicationType = ref<'none' | 'honor' | 'memory'>('none')
    const sponsorshipDate = ref('Tue, Sep 15, 2026 — Zeraim, Brachos')

    const giftTypes = [
      { id: 'day' as const, label: 'Sponsor a day', amountLabel: '$360' },
      { id: 'tractate' as const, label: 'Sponsor a tractate', amountLabel: '$1,800' },
      { id: 'general' as const, label: 'Give any amount', amountLabel: 'Your choice' },
    ]

    const dedicationChoices = [
      { id: 'none' as const, label: 'No dedication' },
      { id: 'honor' as const, label: 'In honor of' },
      { id: 'memory' as const, label: 'In memory of' },
    ]

    const formattedAmount = computed(() => {
      const currency = form.value.currency || 'USD'
      const amount = Number(form.value.amount || 0)
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      }).format(amount)
    })

    const donorName = computed(() => {
      const sponsorName = form.value.custom?.SponsorName
      if (sponsorName) return sponsorName
      const name = [form.value.firstName, form.value.lastName].filter(Boolean).join(' ')
      return name || '[Your Name]'
    })

    const dedicationText = computed(() => {
      if (dedicationType.value === 'none') return ''
      const dedication = form.value.custom?.Dedication
      if (!dedication) return ''
      return `${dedicationType.value === 'honor' ? 'In honor of' : 'In memory of'} ${dedication}.`
    })

    const selectGift = (gift: 'day' | 'tractate' | 'general') => {
      activeGift.value = gift
      setValue('amount', gift === 'day' ? '360' : gift === 'tractate' ? '1800' : '360')
    }

    const selectDedication = (type: 'none' | 'honor' | 'memory') => {
      dedicationType.value = type
      setValue('custom.DedicationType', type)
      if (type === 'none') setValue('custom.Dedication', '')
    }

    const selectSponsorshipDate = (event: Event) => {
      sponsorshipDate.value = (event.target as HTMLSelectElement).value
      setValue('custom.SponsorshipDate', sponsorshipDate.value)
    }

    setValue('amount', '360')
    setValue('custom.SponsorshipDate', sponsorshipDate.value)

    return {
      activeGift,
      dedicationChoices,
      dedicationText,
      dedicationType,
      donorName,
      formattedAmount,
      giftTypes,
      selectDedication,
      selectGift,
      selectSponsorshipDate,
      sponsorshipDate,
    }
  },
})
</script>

<style scoped lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Hind:wght@400;500;600&family=Instrument+Serif:ital@0;1&display=swap');

.talmud-give {
  --ink: #172020;
  --paper: #eae2d0;
  --paper-pure: #f2eddf;
  --burgundy: #500e0f;
  --muted: #5e5c50;
  --rule: #c9bfa6;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(280px, 1fr);
  gap: 72px;
  align-items: start;
  max-width: 1240px;
  margin: 0 auto;
  padding: 64px 48px 96px;
  color: var(--ink);
  background: var(--paper);
  font-family: 'Hind', sans-serif;
  font-size: 17px;
  line-height: 1.55;
}

.talmud-give,
.talmud-give * {
  box-sizing: border-box;
}

.tabs {
  display: flex;
  margin-bottom: 40px;
  border-bottom: 1px solid var(--rule);
}

.tab {
  position: relative;
  margin-right: 40px;
  padding: 18px 4px;
  border: 0;
  background: transparent;
  color: var(--muted);
  font: 600 13px/1.3 'Hind', sans-serif;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  cursor: pointer;
}

.tab.active {
  color: var(--burgundy);
}

.tab.active::after {
  position: absolute;
  right: 0;
  bottom: -1px;
  left: 0;
  height: 2px;
  background: var(--burgundy);
  content: '';
}

.tab .amt {
  display: block;
  margin-top: 4px;
  color: inherit;
  font: italic 20px/1.2 'Instrument Serif', Georgia, serif;
  letter-spacing: 0;
  text-transform: none;
}

.field-group {
  margin-bottom: 32px;
}

.section-label,
:deep(.field-group > h2) {
  margin: 0 0 10px;
  color: var(--burgundy);
  font: 600 11px/1.4 'Hind', sans-serif;
  letter-spacing: 0.28em;
  text-transform: uppercase;
}

.helper {
  margin: 0 0 14px;
  color: var(--muted);
  font: italic 15px/1.4 'Instrument Serif', Georgia, serif;
}

.fixed-amount {
  display: flex;
  gap: 20px;
  align-items: baseline;
  padding: 20px 24px;
  border: 1px solid var(--rule);
  border-left: 3px solid var(--burgundy);
  background: var(--paper-pure);
}

.fixed-amount .amt {
  flex: none;
  color: var(--burgundy);
  font: 40px/1 'Instrument Serif', Georgia, serif;
}

.amt-note {
  color: var(--muted);
  font: italic 15px/1.35 'Instrument Serif', Georgia, serif;
}

.choice-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.choice {
  padding: 12px 18px;
  border: 1px solid var(--rule);
  border-radius: 2px;
  background: transparent;
  color: var(--muted);
  font: 500 14px/1.4 'Hind', sans-serif;
  cursor: pointer;
}

.choice:hover,
.choice.active {
  border-color: var(--burgundy);
  color: var(--burgundy);
}

.choice.active {
  background: var(--burgundy);
  color: var(--paper);
}

:deep(.dedication-input) {
  margin-top: 12px;
}

:deep(.field-group input),
:deep(.field-group textarea),
:deep(.field-group select),
:deep(.field-group .vs__dropdown-toggle) {
  border-color: var(--rule) !important;
  border-radius: 1px !important;
  background: var(--paper-pure) !important;
}

:deep(.field-group input:focus),
:deep(.field-group textarea:focus),
:deep(.field-group select:focus) {
  border-color: var(--ink) !important;
  box-shadow: none !important;
}

:deep(.amount-group .styled-radio--ou-donation label) {
  border-color: var(--rule);
  border-radius: 2px;
  background: transparent;
  color: var(--ink);
  font: 20px/1.2 'Instrument Serif', Georgia, serif;
}

:deep(.amount-group .styled-radio--ou-donation input[type='radio']:checked + label) {
  border-color: var(--burgundy);
  background: var(--burgundy);
  color: var(--paper);
}

.payment-section {
  margin-bottom: 0;
}

.submit-row {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: center;
  justify-content: space-between;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid var(--rule);
}

.submit-note {
  max-width: 30ch;
  margin: 0;
  color: var(--muted);
  font: italic 14px/1.4 'Instrument Serif', Georgia, serif;
}

:deep(.submit-row .payment) {
  margin: 0;
}

:deep(.submit-row .ou-donation-btn) {
  min-height: 54px;
  padding: 16px 28px;
  border: 0;
  border-radius: 1px;
  background: var(--burgundy);
  color: var(--paper);
  font: 600 14px/1.2 'Hind', sans-serif;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.preview-col {
  position: sticky;
  top: 96px;
}

.preview {
  padding: 32px 28px;
  border: 1px solid var(--rule);
  background: var(--paper-pure);
}

.p-eyebrow,
.p-panel-label {
  margin-bottom: 12px;
  color: var(--burgundy);
  font: 600 10px/1.4 'Hind', sans-serif;
  letter-spacing: 0.28em;
  text-transform: uppercase;
}

.p-title {
  margin: 0 0 4px;
  color: var(--ink);
  font: 22px/1.2 'Instrument Serif', Georgia, serif;
}

.p-sub {
  margin: 0 0 26px;
  color: var(--muted);
  font: 500 12px/1.4 'Hind', sans-serif;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.p-panel {
  margin-bottom: 18px;
  padding: 18px 22px;
  border-left: 3px solid var(--burgundy);
  background: var(--paper);
}

.p-panel-label {
  margin-bottom: 8px;
  letter-spacing: 0.24em;
}

.p-line,
.p-dedication,
.p-note {
  margin: 0;
  color: var(--muted);
  font-family: 'Instrument Serif', Georgia, serif;
  font-style: italic;
}

.p-line {
  color: var(--ink);
  font-size: 18px;
  line-height: 1.35;
}

.p-line em,
.p-dedication {
  color: var(--burgundy);
}

.p-dedication {
  margin-top: 4px;
  font-size: 15px;
}

.p-note {
  font-size: 13px;
  line-height: 1.4;
}

.amt-summary {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--rule);
  color: var(--muted);
  font: 600 12px/1.4 'Hind', sans-serif;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.amt-summary .total {
  color: var(--burgundy);
  font: 32px/1 'Instrument Serif', Georgia, serif;
  letter-spacing: 0;
  text-transform: none;
}

@media (max-width: 900px) {
  .talmud-give {
    grid-template-columns: 1fr;
    gap: 48px;
    padding: 40px 24px 64px;
  }

  .preview-col {
    position: static;
  }

  .tabs {
    flex-wrap: wrap;
  }

  .tab {
    margin-right: 16px;
    padding: 14px 4px;
  }
}

@media (max-width: 560px) {
  .talmud-give {
    padding-right: 18px;
    padding-left: 18px;
  }

  .tab {
    width: 100%;
    margin-right: 0;
    text-align: left;
  }

  .fixed-amount,
  .submit-row {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
